import {
  Count,
  SectionProgress,
  SectionStatus,
  SectionStatusStatuses,
} from '../../entities/installation-reporting-mdu.entities';

export interface StatusDisplay {
  labelKey: string | null;
  iconName: 'clock' | 'circleCheck' | 'circleClose' | 'triangleExclamation';
  color: 'active' | 'caution' | 'critical' | 'success' | 'secondary';
}

export const STATUS_LABEL_KEYS: Record<string, string> = {
  IN_PROGRESS: 'installationReportingMduTable.status.inProgress',
  DONE: 'workSectionStatus.EXECUTED',
  EXECUTED: 'workSectionStatus.EXECUTED',
  PLANNED: 'workSectionStatus.PLANNED',
  NOT_STARTED: 'workSectionStatus.AWAITING_PLANNING',
  INCOMPLETE: 'workSectionStatus.INCOMPLETE',
  NOT_APPLICABLE: 'buildingPageModal.sectionNotApplicable.tag',
};

export const getStatusDisplay = (status?: string | null): StatusDisplay => {
  const labelKey = status
    ? (STATUS_LABEL_KEYS[status] ?? 'ordersOverview.latestStatus.UNKNOWN')
    : null;

  switch (status) {
    case 'IN_PROGRESS':
      return { labelKey, iconName: 'clock', color: 'active' };
    case 'PLANNED':
      return { labelKey, iconName: 'clock', color: 'caution' };
    case 'DONE':
    case 'EXECUTED':
      return { labelKey, iconName: 'circleCheck', color: 'success' };
    case 'NOT_STARTED':
      return { labelKey, iconName: 'circleClose', color: 'secondary' };
    case 'INCOMPLETE':
      return { labelKey, iconName: 'triangleExclamation', color: 'critical' };
    default:
      return { labelKey, iconName: 'circleClose', color: 'secondary' };
  }
};

export const getSectionStatusValue = (
  section: SectionStatus | null | undefined,
): SectionStatusStatuses | undefined => {
  const status = section?.status;
  return typeof status === 'string'
    ? (status as SectionStatusStatuses)
    : undefined;
};

export const getSectionStatusLabelKey = (
  section: SectionStatus | null | undefined,
): string => {
  const { labelKey } = getStatusDisplay(getSectionStatusValue(section));
  return labelKey ?? 'ordersOverview.latestStatus.UNKNOWN';
};

export const getSectionStatusDate = (
  section: SectionStatus | null | undefined,
): string | undefined => {
  const date = section?.completedDate ?? section?.updatedAt;
  return date || undefined;
};

const progressCounts = (
  progress: SectionProgress | null | undefined,
): Count[] =>
  [progress?.building, progress?.deliveryPoint].filter(
    (count): count is Count => !!count,
  );

/** Derives a section status from building/delivery point done/total counts. */
export const getSectionProgressValue = (
  progress: SectionProgress | null | undefined,
): SectionStatusStatuses | undefined => {
  const counts = progressCounts(progress);
  const total = counts.reduce((sum, count) => sum + count.total, 0);
  const done = counts.reduce((sum, count) => sum + count.done, 0);
  if (counts.length === 0 || total === 0) {
    return SectionStatusStatuses.NOT_STARTED;
  }
  if (done >= total) {
    return SectionStatusStatuses.DONE;
  }
  return done === 0
    ? SectionStatusStatuses.NOT_STARTED
    : SectionStatusStatuses.IN_PROGRESS;
};

export const getSectionProgressLabelKey = (
  progress: SectionProgress | null | undefined,
): string =>
  getStatusDisplay(getSectionProgressValue(progress)).labelKey ??
  'ordersOverview.latestStatus.UNKNOWN';

export interface SectionProgressLine {
  icon: 'house' | 'plugConnected';
  text: string;
  tone: 'done' | 'none' | 'partial';
}

const toProgressLine = (
  icon: SectionProgressLine['icon'],
  count: Count,
): SectionProgressLine => ({
  icon,
  text: `${count.done}/${count.total}`,
  tone:
    count.total > 0 && count.done >= count.total
      ? 'done'
      : count.done === 0
        ? 'none'
        : 'partial',
});

/** One line per count: house for buildings, plug for delivery points. */
export const getSectionProgressLines = (
  progress: SectionProgress | null | undefined,
): SectionProgressLine[] =>
  [
    progress?.building ? toProgressLine('house', progress.building) : null,
    progress?.deliveryPoint
      ? toProgressLine('plugConnected', progress.deliveryPoint)
      : null,
  ].filter((line): line is SectionProgressLine => !!line);
